import { cs } from '../../../lib/currency';
/**
 * Aged Receivables report — Billing Management → "Aged AR" tab (iter275).
 *
 * Pulls `/admin/billing/aged-receivables` which groups every overdue
 * invoice (across enrollments, lane bookings, tournaments, small group
 * sessions, and manual invoices) by parent/customer and aging bucket
 * (0-30 / 31-60 / 61-90 / 90+ days).
 *
 * Hyperlinks on the parent name take staff straight to the parent
 * detail page where the new `ParentInvoicesPanel` (iter272) surfaces
 * the matching overdue rows with per-row Charge / Send Reminder /
 * Mark Paid actions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import {
  AlertTriangle, Loader2, RefreshCw, Search, Download, ArrowUp, ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';
import { toQueryParams } from './DateRangePicker';
import TablePaginationFooter from '../../../components/admin/TablePaginationFooter';

const BUCKET_CARDS = [
  { key: 'b_0_30', label: '0-30 days', tone: 'border-amber-200 bg-amber-50 text-amber-900' },
  { key: 'b_31_60', label: '31-60 days', tone: 'border-orange-200 bg-orange-50 text-orange-900' },
  { key: 'b_61_90', label: '61-90 days', tone: 'border-red-200 bg-red-50 text-red-900' },
  { key: 'b_90_plus', label: '90+ days', tone: 'border-rose-300 bg-rose-50 text-rose-900' },
];

// iter276: every column header is sortable. We keep the column → row
// field map here in one place so the header click handler and the sort
// comparator both agree on what field they're working on. `kind` drives
// the comparator branch (numbers vs strings vs array length).
const COLUMNS = [
  { key: 'parent_name', label: 'Parent / Customer', kind: 'str', align: 'left' },
  { key: 'b_0_30', label: '0-30 days', kind: 'num', align: 'right' },
  { key: 'b_31_60', label: '31-60 days', kind: 'num', align: 'right' },
  { key: 'b_61_90', label: '61-90 days', kind: 'num', align: 'right' },
  { key: 'b_90_plus', label: '90+ days', kind: 'num', align: 'right' },
  { key: 'total', label: 'Total', kind: 'num', align: 'right' },
  { key: 'count', label: 'Invoices', kind: 'num', align: 'center' },
  { key: 'oldest_days', label: 'Oldest', kind: 'num', align: 'right' },
  { key: 'invoice_types', label: 'Types', kind: 'list_len', align: 'left' },
];

const fmtMoney = (n) => `${cs()}${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AgedReceivablesTab({ dateRange, locationId } = {}) {
  const [data, setData] = useState({ rows: [], totals: null, as_of: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQ, setSearchQ] = useState('');
  // iter276: column-header sort. `sortKey` is one of the COLUMNS keys
  // above; clicking the same header twice flips direction. Default
  // matches the previous "total desc" behaviour so the highest-value
  // debtors stay at the top on first open.
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // iter384 — honor the shared Billing Management filter bar (period +
      // location) so this report reconciles with the Overview + Invoice
      // Management tabs instead of always showing all-time / all-location.
      const params = toQueryParams(dateRange);
      if (locationId && locationId !== 'all') params.append('location_id', locationId);
      const qs = params.toString();
      const res = await api.get(`/admin/billing/aged-receivables${qs ? `?${qs}` : ''}`);
      setData(res.data || { rows: [], totals: null });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load aged receivables');
    } finally {
      setLoading(false);
    }
  }, [dateRange, locationId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [searchQ, sortKey, sortDir]);

  // Toggle direction when clicking the same column; otherwise switch to
  // the new column with a sensible default direction (desc for numeric
  // columns, asc for the name).
  const onSort = (colKey) => {
    if (sortKey === colKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    const col = COLUMNS.find((c) => c.key === colKey);
    setSortKey(colKey);
    setSortDir(col && col.kind === 'str' ? 'asc' : 'desc');
  };

  // Filter + sort happens client-side — the dataset is bounded by the
  // number of *parents* with overdue debt, which is small (tens, not
  // thousands), so a single server call is plenty.
  const filteredRows = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    const rows = q
      ? (data.rows || []).filter((r) => (
        (r.parent_name || '').toLowerCase().includes(q)
          || (r.parent_email || '').toLowerCase().includes(q)
      ))
      : (data.rows || []);

    const col = COLUMNS.find((c) => c.key === sortKey) || COLUMNS[5];
    const dirMul = sortDir === 'asc' ? 1 : -1;
    const getVal = (row) => {
      if (col.kind === 'num') return Number(row[col.key]) || 0;
      if (col.kind === 'list_len') return (row[col.key] || []).length;
      return (row[col.key] || '').toString().toLowerCase();
    };

    const sorted = [...rows].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });
    return sorted;
  }, [data.rows, searchQ, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginated = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  // CSV export — one row per parent so finance can paste straight into
  // a follow-up spreadsheet / collections tracker.
  const exportCsv = () => {
    if (!filteredRows.length) { toast.info('Nothing to export'); return; }
    const header = ['Parent / Customer', 'Email', '0-30', '31-60', '61-90', '90+', 'Total', 'Invoices', 'Oldest (days)', 'Types'];
    const lines = [header.join(',')];
    for (const r of filteredRows) {
      lines.push([
        JSON.stringify(r.parent_name || ''),
        JSON.stringify(r.parent_email || ''),
        r.b_0_30.toFixed(2),
        r.b_31_60.toFixed(2),
        r.b_61_90.toFixed(2),
        r.b_90_plus.toFixed(2),
        r.total.toFixed(2),
        r.count,
        r.oldest_days,
        JSON.stringify((r.invoice_types || []).join('|')),
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aged-receivables-${data.as_of || 'today'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !data.totals) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Loading aged receivables…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
        <p className="text-red-600">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load}>Retry</Button>
      </div>
    );
  }

  const totals = data.totals || {
    b_0_30: 0, b_31_60: 0, b_61_90: 0, b_90_plus: 0, total: 0, count: 0,
  };

  return (
    <div className="space-y-4" data-testid="aged-ar-tab">
      {/* Header: title + meta + actions */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Aged Receivables</h2>
          <p className="text-xs text-slate-500">
            Outstanding invoices grouped by parent/customer and aging bucket.
            As of <span className="font-medium">{data.as_of || '—'}</span>.
            {data.scope && (
              <span className="font-medium text-slate-600">
                {' '}Scope: {data.scope.period_label || 'All time'}
                {data.scope.location_id ? ' · selected location' : ' · all locations'}.
              </span>
            )}
            {' '}{totals.count} overdue invoice{totals.count === 1 ? '' : 's'} across
            {' '}{filteredRows.length} {filteredRows.length === 1 ? 'party' : 'parties'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="aged-ar-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            data-testid="aged-ar-export-csv"
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Bucket summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="aged-ar-summary">
        {BUCKET_CARDS.map(({ key, label, tone }) => (
          <Card key={key} className={`border ${tone}`}>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</p>
              <p
                className="text-2xl font-bold mt-1"
                data-testid={`aged-ar-bucket-${key}`}
              >
                {fmtMoney(totals[key])}
              </p>
            </CardContent>
          </Card>
        ))}
        <Card className="border border-slate-300 bg-slate-50 text-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide opacity-80">Total outstanding</p>
            <p
              className="text-2xl font-bold mt-1"
              data-testid="aged-ar-bucket-total"
            >
              {fmtMoney(totals.total)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter row — sort lives on the column headers now (iter276). */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search by parent name or email"
            className="pl-9 h-9"
            data-testid="aged-ar-search"
          />
        </div>
        <span className="text-xs text-slate-400 ml-1">
          Tip: click a column header to sort. Click again to reverse.
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredRows.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <p className="font-medium">No overdue invoices</p>
              <p className="text-xs mt-1">
                {searchQ ? 'No parents match your search.' : 'Everyone is paid up.'}
              </p>
            </div>
          ) : (
            <>
              <Table data-testid="aged-ar-table">
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((col) => {
                      const active = sortKey === col.key;
                      const Icon = !active
                        ? ArrowUpDown
                        : (sortDir === 'asc' ? ArrowUp : ArrowDown);
                      const alignClass = col.align === 'right'
                        ? 'text-right'
                        : (col.align === 'center' ? 'text-center' : 'text-left');
                      return (
                        <TableHead key={col.key} className={alignClass}>
                          <button
                            type="button"
                            onClick={() => onSort(col.key)}
                            className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${
                              active ? 'text-slate-900 font-semibold' : 'text-slate-500'
                            } ${col.align === 'right' ? 'flex-row-reverse' : ''}`}
                            data-testid={`aged-ar-sort-${col.key}`}
                            aria-label={`Sort by ${col.label}`}
                            aria-sort={
                              active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                            }
                          >
                            <Icon
                              className={`h-3.5 w-3.5 ${active ? 'opacity-100' : 'opacity-40'}`}
                            />
                            <span>{col.label}</span>
                          </button>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((row) => (
                    <TableRow
                      key={row.parent_id}
                      data-testid={`aged-ar-row-${row.parent_id}`}
                    >
                      <TableCell>
                        {/* Hyperlink to the parent / customer profile.
                            ParentDetail.js renders for both `parent`
                            and `customer` roles, so we can route to it
                            regardless of which the row is. */}
                        <Link
                          to={`/admin/parents/${row.parent_id}?tab=billing`}
                          className="text-blue-600 hover:underline font-medium"
                          data-testid={`aged-ar-name-link-${row.parent_id}`}
                        >
                          {row.parent_name || row.parent_email || row.parent_id || 'Unknown'}
                        </Link>
                        {row.parent_email && (
                          <p className="text-xs text-slate-400 mt-0.5">{row.parent_email}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.b_0_30 > 0 ? fmtMoney(row.b_0_30) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.b_31_60 > 0
                          ? <span className="text-orange-700 font-medium">{fmtMoney(row.b_31_60)}</span>
                          : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.b_61_90 > 0
                          ? <span className="text-red-700 font-medium">{fmtMoney(row.b_61_90)}</span>
                          : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.b_90_plus > 0
                          ? <span className="text-rose-700 font-bold">{fmtMoney(row.b_90_plus)}</span>
                          : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {fmtMoney(row.total)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{row.count}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span className={
                          row.oldest_days > 90 ? 'text-rose-700 font-medium'
                            : row.oldest_days > 60 ? 'text-red-700'
                              : row.oldest_days > 30 ? 'text-orange-700'
                                : 'text-slate-600'
                        }
                        >
                          {row.oldest_days}d
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(row.invoice_types || []).map((t) => (
                            <Badge
                              key={t}
                              variant="secondary"
                              className="text-[10px] uppercase tracking-wide"
                            >
                              {t.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePaginationFooter
                currentPage={page}
                totalPages={totalPages}
                itemsPerPage={pageSize}
                totalItems={filteredRows.length}
                itemLabel="party"
                onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
                onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
                onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
                testIdPrefix="aged-ar-pagination"
              />
            </>
          )}
        </CardContent>
      </Card>

      {data.unparseable > 0 && (
        <p className="text-xs text-slate-400">
          Note: {data.unparseable} overdue invoice{data.unparseable === 1 ? '' : 's'} could not be aged
          (missing both due_date and created_at) and {data.unparseable === 1 ? 'is' : 'are'} excluded.
        </p>
      )}
    </div>
  );
}
